import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../services/api';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
  BarChart,
} from 'recharts';

type Period = 'week' | 'month' | 'quarter' | 'year';
type View = 'account' | 'platform';

const PERIODS: { label: string; value: Period }[] = [
  { label: '1W', value: 'week' },
  { label: '1M', value: 'month' },
  { label: '3M', value: 'quarter' },
  { label: '1Y', value: 'year' },
];

const VIEWS: { label: string; value: View }[] = [
  { label: 'Account Performance', value: 'account' },
  { label: 'Gogo Platform', value: 'platform' },
];

const BROKER_BADGE: Record<string, string> = {
  kite: 'bg-blue-50 text-blue-700',
  fyers: 'bg-purple-50 text-purple-700',
  upstox: 'bg-orange-50 text-orange-700',
  alpaca: 'bg-gray-50 text-gray-700',
};

function formatINR(v: number) {
  return `₹${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function signedINR(v: number) {
  return `${v >= 0 ? '+' : '-'}${formatINR(v)}`;
}

const Analytics: React.FC = () => {
  const [period, setPeriod] = useState<Period>('quarter');
  const [view, setView] = useState<View>('account');

  const { data: accountPerfData, isLoading: accountPerfLoading } = useQuery({
    queryKey: ['analytics-account-performance'],
    queryFn: () => analyticsAPI.getAccountPerformance().then((r) => r.data),
    enabled: view === 'account',
  });

  const { data: pnlData, isLoading: pnlLoading } = useQuery({
    queryKey: ['analytics-pnl', period],
    queryFn: () => analyticsAPI.getPnl({ period }).then((r) => r.data),
    enabled: view === 'platform',
  });

  const { data: indexData, isLoading: indexLoading } = useQuery({
    queryKey: ['analytics-index', period],
    queryFn: () => analyticsAPI.getIndexComparison({ period }).then((r) => r.data),
    enabled: view === 'platform',
  });

  const isLoading = view === 'account' ? accountPerfLoading : (pnlLoading || indexLoading);

  // Build chart data for platform view
  const chartData = React.useMemo(() => {
    if (!pnlData?.daily_pnl?.length) return [];
    return pnlData.daily_pnl.map((d: any) => ({
      date: d.date,
      return_pct: pnlData.total_pnl !== 0 && indexData?.total_invested
        ? (d.daily_pnl / indexData.total_invested) * 100
        : 0,
      cumulative_balance: (indexData?.total_invested || 0) + d.cumulative_pnl,
      daily_pnl: d.daily_pnl,
    }));
  }, [pnlData, indexData]);

  const portfolioReturnPct = indexData?.portfolio_return_pct || 0;
  const totalInvested = indexData?.total_invested || 0;

  // Staleness indicator for account performance
  const syncedAt = accountPerfData?.synced_at ? new Date(accountPerfData.synced_at) : null;
  const hoursStale = syncedAt ? (Date.now() - syncedAt.getTime()) / 3600000 : 0;
  const staleClass = hoursStale > 24 ? 'text-amber-500' : 'text-gray-400';
  const syncedLabel = syncedAt
    ? syncedAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="space-y-6">
      {/* Header + View Toggle */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Performance</h1>
        <div className="flex bg-gray-100 rounded-full p-1">
          {VIEWS.map((v) => (
            <button
              key={v.value}
              onClick={() => setView(v.value)}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                view === v.value
                  ? 'bg-green-700 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
        </div>
      ) : (
        <>
          {/* ── ACCOUNT PERFORMANCE VIEW ── */}
          {view === 'account' && (
            <>
              {!accountPerfData?.has_data ? (
                <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                  <p className="text-gray-500 text-lg">No holdings data available.</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Go to Accounts and click "Sync Holdings" on each account to populate this view.
                  </p>
                </div>
              ) : (
                <>
                  {/* KPI Cards */}
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatINR(accountPerfData.total_portfolio_value)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Portfolio Value</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatINR(accountPerfData.total_invested)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Invested</p>
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${accountPerfData.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {signedINR(accountPerfData.total_pnl)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Unrealized P&L</p>
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${accountPerfData.total_return_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {accountPerfData.total_return_pct >= 0 ? '+' : ''}{accountPerfData.total_return_pct.toFixed(2)}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Return</p>
                      </div>
                    </div>
                    {syncedLabel && (
                      <p className={`text-xs mt-4 text-center ${staleClass}`}>
                        Holdings as of {syncedLabel}. Sync from Accounts to refresh.
                      </p>
                    )}
                  </div>

                  {/* By Account */}
                  {accountPerfData.by_account.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4">By Account</h2>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <ResponsiveContainer width="100%" height={Math.max(200, accountPerfData.by_account.length * 50)}>
                          <BarChart data={accountPerfData.by_account} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                            <YAxis type="category" dataKey="account_name" tick={{ fontSize: 11 }} width={120} />
                            <Tooltip formatter={(value: number) => [formatINR(value)]} />
                            <Bar dataKey="pnl" name="P&L" radius={[0, 4, 4, 0]}>
                              {accountPerfData.by_account.map((entry: any, index: number) => (
                                <Cell key={index} fill={entry.pnl >= 0 ? '#16a34a' : '#dc2626'} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>

                        <div className="overflow-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Account</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Invested</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Value</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">P&L</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Return</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Holdings</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {accountPerfData.by_account.map((a: any) => (
                                <tr key={a.account_key}>
                                  <td className="px-3 py-2">
                                    <span className="font-medium">{a.account_name}</span>
                                    <span className={`ml-2 inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${BROKER_BADGE[a.broker_type] || BROKER_BADGE.alpaca}`}>
                                      {a.broker_type}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-600">{formatINR(a.total_invested)}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">{formatINR(a.current_value)}</td>
                                  <td className={`px-3 py-2 text-right font-mono font-medium ${a.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {signedINR(a.pnl)}
                                  </td>
                                  <td className={`px-3 py-2 text-right font-medium ${a.return_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {a.return_pct >= 0 ? '+' : ''}{a.return_pct.toFixed(2)}%
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-500">{a.holding_count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Holdings Breakdown: Top Winners / Losers */}
                  {(accountPerfData.top_winners.length > 0 || accountPerfData.top_losers.length > 0) && (
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4">Holdings Breakdown</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h3 className="text-sm font-semibold text-green-700 mb-2">Top Winners</h3>
                          <div className="space-y-1">
                            {accountPerfData.top_winners.map((h: any, i: number) => (
                              <div key={i} className="flex justify-between items-center py-1.5 px-2 rounded hover:bg-gray-50">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{h.tradingsymbol}</p>
                                  <p className="text-xs text-gray-400">{h.account_name} · {h.broker_type}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-mono text-green-600">+{formatINR(h.pnl)}</p>
                                  <p className="text-xs text-green-500">+{h.return_pct.toFixed(2)}%</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-red-700 mb-2">Top Losers</h3>
                          <div className="space-y-1">
                            {accountPerfData.top_losers.map((h: any, i: number) => (
                              <div key={i} className="flex justify-between items-center py-1.5 px-2 rounded hover:bg-gray-50">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{h.tradingsymbol}</p>
                                  <p className="text-xs text-gray-400">{h.account_name} · {h.broker_type}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-mono text-red-600">-{formatINR(h.pnl)}</p>
                                  <p className="text-xs text-red-500">{h.return_pct.toFixed(2)}%</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── GOGO PLATFORM VIEW ── */}
          {view === 'platform' && (
            <>
              {/* Period Selector */}
              <div className="flex justify-end">
                <div className="flex bg-gray-100 rounded-full p-1">
                  {PERIODS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setPeriod(p.value)}
                      className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                        period === p.value
                          ? 'bg-green-700 text-white'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Balance + Return Header */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-sm text-gray-500 font-medium mb-1">Portfolio Value</h2>
                    <p className="text-3xl font-bold text-gray-900">
                      ₹{(totalInvested + (pnlData?.total_pnl || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                    <p className={`text-sm mt-1 font-medium ${(pnlData?.total_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(pnlData?.total_pnl || 0) >= 0 ? '+' : ''}₹{Math.abs(pnlData?.total_pnl || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      {' '}({portfolioReturnPct >= 0 ? '+' : ''}{portfolioReturnPct.toFixed(2)}%)
                    </p>
                  </div>
                  <div>
                    <h2 className="text-sm text-gray-500 font-medium mb-1">Return</h2>
                    <p className={`text-3xl font-bold ${portfolioReturnPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {portfolioReturnPct >= 0 ? '+' : ''}{portfolioReturnPct.toFixed(2)}%
                    </p>
                    <div className="flex gap-6 mt-2 text-sm text-gray-500">
                      <span>Invested: <strong className="text-gray-900">₹{totalInvested.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong></span>
                      <span>Win Rate: <strong className="text-gray-900">{pnlData?.win_rate || 0}%</strong></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Chart: Balance line + Return bars */}
              {chartData.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(d) => {
                          const parts = d.split('-');
                          return `${parts[1]}/${parts[2]}`;
                        }}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v) => `${v.toFixed(1)}%`}
                        label={{ value: 'Return', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`}
                        label={{ value: 'Balance', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => {
                          if (name === 'Return %') return [`${value.toFixed(2)}%`, name];
                          if (name === 'Balance') return [`₹${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, name];
                          return [`₹${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, name];
                        }}
                        labelFormatter={(label) => label}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="return_pct" name="Return %" radius={[2, 2, 0, 0]}>
                        {chartData.map((entry: any, index: number) => (
                          <Cell key={index} fill={entry.daily_pnl >= 0 ? '#16a34a' : '#dc2626'} />
                        ))}
                      </Bar>
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cumulative_balance"
                        name="Balance"
                        stroke="#1f2937"
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Temporal Comparison Chart */}
              {indexData?.daily_comparison?.length > 0 && (
                <TemporalComparisonChart
                  data={indexData.daily_comparison}
                  accounts={indexData.available_accounts || []}
                />
              )}

              {/* Total Returns Comparison Table */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Total Returns</h2>
                    <p className="text-sm text-gray-500">
                      {pnlData?.start_date} to {pnlData?.end_date}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-4">Time-weighted rate of return</p>
                <div className="divide-y divide-gray-100">
                  <div className="flex justify-between py-3">
                    <span className="font-semibold text-gray-900">Your return</span>
                    <span className={`font-bold ${portfolioReturnPct >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {portfolioReturnPct >= 0 ? '+' : ''}{portfolioReturnPct.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-gray-700">Nifty 50 Index</span>
                    <span className={`font-medium ${(indexData?.nifty50?.return_pct || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {(indexData?.nifty50?.return_pct || 0) >= 0 ? '+' : ''}{(indexData?.nifty50?.return_pct || 0).toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-gray-700">BSE Sensex</span>
                    <span className={`font-medium ${(indexData?.sensex?.return_pct || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {(indexData?.sensex?.return_pct || 0) >= 0 ? '+' : ''}{(indexData?.sensex?.return_pct || 0).toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-gray-700 font-medium">Alpha vs Nifty 50</span>
                    {(() => {
                      const alpha = portfolioReturnPct - (indexData?.nifty50?.return_pct || 0);
                      return (
                        <span className={`font-bold ${alpha >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {alpha >= 0 ? '+' : ''}{alpha.toFixed(2)}%
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* P&L by Account */}
              {pnlData?.by_account?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">By Account</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={Math.max(200, pnlData.by_account.length * 50)}>
                      <BarChart data={pnlData.by_account} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                        <YAxis type="category" dataKey="account_name" tick={{ fontSize: 11 }} width={120} />
                        <Tooltip formatter={(value: number) => [`₹${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`]} />
                        <Bar dataKey="pnl" name="P&L" radius={[0, 4, 4, 0]}>
                          {pnlData.by_account.map((entry: any, index: number) => (
                            <Cell key={index} fill={entry.pnl >= 0 ? '#16a34a' : '#dc2626'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="overflow-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Account</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">P&L</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">W/L</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Trades</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {pnlData.by_account.map((a: any) => (
                            <tr key={a.account_key}>
                              <td className="px-3 py-2">
                                <span className="font-medium">{a.account_name}</span>
                                <span className={`ml-2 inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${BROKER_BADGE[a.broker_type] || BROKER_BADGE.alpaca}`}>
                                  {a.broker_type}
                                </span>
                              </td>
                              <td className={`px-3 py-2 text-right font-mono font-medium ${a.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {a.pnl >= 0 ? '+' : ''}₹{Math.abs(a.pnl).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <span className="text-green-600">{a.win_count}</span>/<span className="text-red-600">{a.loss_count}</span>
                              </td>
                              <td className="px-3 py-2 text-right text-gray-500">{a.trade_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Top Performers */}
              {pnlData?.by_symbol?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">By Symbol</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-green-700 mb-2">Top Winners</h3>
                      <div className="space-y-1">
                        {pnlData.by_symbol.filter((s: any) => s.pnl > 0).slice(0, 8).map((s: any) => (
                          <div key={s.symbol} className="flex justify-between items-center py-1.5 px-2 rounded hover:bg-gray-50">
                            <span className="text-sm font-medium text-gray-900">{s.symbol}</span>
                            <span className="text-sm font-mono text-green-600">+₹{s.pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-red-700 mb-2">Top Losers</h3>
                      <div className="space-y-1">
                        {pnlData.by_symbol.filter((s: any) => s.pnl < 0).slice(-8).reverse().map((s: any) => (
                          <div key={s.symbol} className="flex justify-between items-center py-1.5 px-2 rounded hover:bg-gray-50">
                            <span className="text-sm font-medium text-gray-900">{s.symbol}</span>
                            <span className="text-sm font-mono text-red-600">-₹{Math.abs(s.pnl).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Trade Stats */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Statistics</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{pnlData?.trade_count || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Total Exits</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{pnlData?.win_count || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Winners</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{pnlData?.loss_count || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Losers</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{pnlData?.win_rate || 0}%</p>
                    <p className="text-xs text-gray-500 mt-1">Win Rate</p>
                  </div>
                </div>
              </div>

              {/* Empty state */}
              {(!pnlData?.daily_pnl?.length && !pnlData?.by_symbol?.length) && (
                <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                  <p className="text-gray-500 text-lg">No completed trades in this period.</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Trade data will appear here once you have matched BUY/SELL pairs.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const COLORS = ['#2563eb', '#dc2626', '#f59e0b', '#059669', '#8b5cf6', '#ec4899', '#06b6d4'];

function TemporalComparisonChart({
  data,
  accounts,
}: {
  data: any[];
  accounts: { key: string; name: string }[];
}) {
  const [visibleLines, setVisibleLines] = React.useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {
      portfolio_pct: true,
      nifty_pct: true,
      sensex_pct: true,
    };
    accounts.forEach((a) => { initial[a.key] = false; });
    return initial;
  });

  const toggleLine = (key: string) => {
    setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allSeries = [
    { key: 'portfolio_pct', name: 'Portfolio (All)', color: '#2563eb' },
    { key: 'nifty_pct', name: 'Nifty 50', color: '#dc2626' },
    { key: 'sensex_pct', name: 'Sensex', color: '#f59e0b' },
    ...accounts.map((a, i) => ({
      key: a.key,
      name: a.name,
      color: COLORS[(i + 3) % COLORS.length],
    })),
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-2">Performance Comparison (% Return)</h2>
      <p className="text-xs text-gray-500 mb-4">Select which lines to display</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {allSeries.map((s) => (
          <button
            key={s.key}
            onClick={() => toggleLine(s.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              visibleLines[s.key]
                ? 'text-white border-transparent'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
            style={visibleLines[s.key] ? { backgroundColor: s.color } : {}}
          >
            {s.name}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            tickFormatter={(d) => d.slice(5)}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            tickFormatter={(v) => `${v.toFixed(1)}%`}
          />
          <Tooltip
            formatter={(value: number, name: string) => [`${value.toFixed(2)}%`, name]}
            labelFormatter={(label) => `Date: ${label}`}
          />
          {allSeries.map((s) =>
            visibleLines[s.key] ? (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={s.key === 'portfolio_pct' ? 2.5 : 1.5}
                dot={false}
                strokeDasharray={s.key.includes('pct') && s.key !== 'portfolio_pct' ? '4 2' : undefined}
              />
            ) : null
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default Analytics;
